//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useState, useCallback, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import startCase from 'lodash/startCase'

import * as sessionUserSelector from 'selectors/sessionUser'
import { getFieldValue } from 'utils/ticketFields'
import { validateMac } from 'utils/strings'
import { useCheckFeature } from 'utils/hooks'

import { useParentField } from './hooks'

import PageForm from 'components/Shared/PageForm/Index'
import ReadOnlyFieldsToggler from './ReadOnlyFieldsToggler'

const StyledPageForm = styled(PageForm)`
  margin-top: 24px;
`

const getFieldType = (field, isEditable, IS_RELAY_3134_RICHTEXT_EDITOR) => {
  if (!isEditable) {
    if (IS_RELAY_3134_RICHTEXT_EDITOR && field.fieldType === 'text_jira') {
      return 'text_jira'
    }
    return 'readOnlyField'
  }

  const origType = field.fieldType
  let type = 'textarea'
  if (origType === 'datetime' || origType === 'integer' || (IS_RELAY_3134_RICHTEXT_EDITOR && origType === 'text_jira')) {
    type = origType
  } else if (origType === 'array_csv') {
    type = 'creatable_array'
  } else if (origType === 'array') {
    // select with isMultiple
    type = 'select'
  } else if (field.allowedValues?.length > 0) {
    type = 'select'
  }

  return type
}

const macValidateFunc = (value) => {
  if (!validateMac(value)) {
    return ({ type: 'MAC_ERROR', highlight: true })
  }
}

// Maps what fields to make read-only when the specified type is selected (case is important!)
const READONLY_BY_TYPE_MAP = {
  'Sub-task': ['sprint'],
  // this value is requited for Edit form to match the value from the backend.
  // TODO: check if changes are needed on backend
  'Sub-Task': ['sprint'],
}

const TicketForm = ({
  fields: originalFields, initialValues, onSubmit, onCancel, isEdit, isClone, withHierarchy, parentTicket,
}) => {
  const { t } = useTranslation()
  const formRef = useRef(null)
  const [readOnlyFieldsHidden, setReadOnlyFieldsHidden] = useState(false)
  const [dynReadOnlyFieldNames, setDynReadOnlyFieldNames] = useState([])

  const userTimeZone = useSelector(sessionUserSelector.timezone)

  const IS_RELAY_3134_RICHTEXT_EDITOR = useCheckFeature('RELAY_3134_RICHTEXT_EDITOR')

  // clone original fields and set dynamic read-only flag depending on the selected type
  // e.g. 'sprint' field for 'sub-task' types
  const fields = useMemo(() => {
    if (dynReadOnlyFieldNames.length === 0) {
      return originalFields
    }

    return originalFields.map(f => {
      if (dynReadOnlyFieldNames.includes(f.name)) {
        return ({
          ...f,
          readOnly: true,
          value: t('ticketForm.inheritedFromParent'),
        })
      }
      return f
    })
  }, [originalFields, dynReadOnlyFieldNames, t])

  // for withHierarchy mode, when type changes
  // make sprint read-only and clear parent select value.
  // This handler is also called in form init (without previousValue)
  const onTypeChange = useCallback((currentValue, previousValue, form) => {
    // we also call it on initial form setup to process
    // a case with a single sub-task option
    setDynReadOnlyFieldNames(READONLY_BY_TYPE_MAP[currentValue] || [])

    // clear sprint because for sub-tasks it should be always empty
    // null will not work here
    if (currentValue?.toLowerCase() === 'sub-task') {
      form.change('sprint', undefined)
    }

    // don't reset read-only parent field (new child ticket)
    const parentField = originalFields.find(f => f.name === 'parent')
    if (parentField && parentField.readOnly) {
      return
    }

    // ignore during initial form setup
    if (previousValue) {
      form.change('parent', null)
    }
  }, [originalFields])

  const { getParentFieldParams } = useParentField({ formRef: formRef })

  const onReadOnlyFieldsChange = (value) => {
    setReadOnlyFieldsHidden(value)
  }

  const getFormFieldParams = () => {
    let hasReadOnlyFields = false
    const fieldParams = fields.map(field => {
      const isEditable = !field.readOnly

      if (!isEditable) {
        hasReadOnlyFields = true
      }

      if (readOnlyFieldsHidden && !isEditable) {
        return null
      }

      if (withHierarchy && field.name === 'parent' && isEditable) {
        const parentFieldParams = getParentFieldParams({
          parentField: field,
        })
        return parentFieldParams
      }

      const type = getFieldType(field, isEditable, IS_RELAY_3134_RICHTEXT_EDITOR)

      const params = {
        key: field.id.toString(),
        name: field.name,
        label: startCase(field.name),
        type: type,
        placeholder: field.placeholder,
        required: field.required,
        tooltip: field.tooltiptext,
      }

      if (withHierarchy && field.name === 'type') {
        params.onChange = onTypeChange
      }

      if (type === 'textarea') {
        params.minRowsTextArea = ['text', 'encrypted_text', 'text_jira'].includes(field.fieldType) ? 6 : 1

        if (field.fieldType === 'dev_mac') {
          params.validateFunc = macValidateFunc
        }
      }

      if (type === 'text_jira') {
        params.minRows = 6
        params.isReadOnly = !isEditable
      }

      if (type === 'datetime') {
        params.userTimeZone = userTimeZone
      }

      if (type === 'select') {
        params.options = [...field.allowedValues]
        params.isMultiselect = field.fieldType === 'array'
      }

      if (type === 'readOnlyField') {
        params.isHtml = Boolean(field.fieldType === 'html')
        if (withHierarchy && field.name === 'parent') {
          params.readOnlyFieldValue = field.value + (parentTicket ? ` ${parentTicket.title}` : '')
        } else {
          params.readOnlyFieldValue = getFieldValue(field, userTimeZone)
        }
      }

      if (type === 'creatable_array') {
        params.maxItems = field.arrayCsvConfig?.limit
        params.allowCsvUpload = true
      }

      return params
    }).filter(v => v) // remove nulls

    return ({
      formFieldParams: fieldParams,
      hasReadOnlyFields: hasReadOnlyFields,
    })
  }

  const getSubmitButtonName = () => {
    const cloneTicketText = t('ticketForm.buttonCloneTicket')
    const createTicketText = t('ticketForm.buttonCreateTicket')
    const name = (isEdit && t('common.update')) || (isClone && cloneTicketText) || createTicketText
    return name
  }

  const { formFieldParams, hasReadOnlyFields } = getFormFieldParams()

  return (
    <>
      {hasReadOnlyFields && (
        <ReadOnlyFieldsToggler
          value={readOnlyFieldsHidden}
          onChange={onReadOnlyFieldsChange}
        />
      )}

      <StyledPageForm
        ref={formRef}
        onSubmit={onSubmit}
        onCancel={onCancel}
        fieldParams={formFieldParams}
        initialValues={initialValues}
        actionButtonTitle={getSubmitButtonName()}
        keepDirtyOnReinitialize
      />
    </>
  )
}

TicketForm.propTypes = {
  fields: PropTypes.array,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  isClone: PropTypes.bool,
  withHierarchy: PropTypes.bool,
  parentTicket: PropTypes.shape({
    id: PropTypes.number,
    number: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
  }),
}

export default TicketForm
